import { FC, useCallback, useEffect, useState } from 'react';

import ReactMarkdown from 'react-markdown';

import { useAppDispatch } from '../../../hooks/redux';
import { useBreakpoint } from '../../../hooks/useBreakpoint';
import { setActiveTool } from '../../../store/meetActiveTool.slice';
import { TipPages } from '../../horizontal-paginator/TipPages';

import Button from '../../common/button';
import { PsyToolsId } from '../../meet/tools-panel/types';
import { loadWheelTips } from '../../../services/tips.api';

import { IPsyBlock } from '../new-questions/new-question-psy';

import { Breakpoint } from '../../../utils/breakpoint';

import { WheelPanel } from './wheelPanel';
import { IPieTipsItem, ITipItem } from './types';
import classNames from 'classnames';

export interface ILifeBalanceWheelPsy extends IPsyBlock {
  sendWheelOpen?: (values: number[]) => void;
  wheelLiveKitEvent?: { nonce: number; kind: 'change' | 'result'; values: number[] } | null;
  onWheelLiveKitEventConsumed?: () => void;
}

export const LifeBalanceWheelPsy: FC<ILifeBalanceWheelPsy> = ({
  result,
  setResult,
  onToolActivated,
  sendWheelOpen,
  wheelLiveKitEvent,
  onWheelLiveKitEventConsumed,
}) => {
  const [tipsLoading, setTipsLoading] = useState(false);
  const [wheelTips, setWheelTips] = useState<IPieTipsItem[]>([]);

  const [active, setActive] = useState(false);
  const [showTips, setShowTips] = useState(false);
  const [selectedTips, setSelectedTips] = useState(1);
  const [isClientSelecting, setIsClientSelecting] = useState(false);

  const breakpoint = useBreakpoint();

  const [wheelValues, setWheelValues] = useState<number[]>([1, 2, 3, 4, 5, 6, 7, 8]);

  const dispatch = useAppDispatch();
  const doSocket = () => {
    sendWheelOpen?.(wheelValues);
    dispatch(setActiveTool(PsyToolsId.WHEEL));
    setIsClientSelecting(true);
  };

  useEffect(() => {
    if (!wheelLiveKitEvent) return;
    if (wheelLiveKitEvent.kind === 'change') {
      setWheelValues(wheelLiveKitEvent.values);
      setIsClientSelecting(true);
    } else {
      if (result) {
        const x = {
          ...result,
          wheel: [
            ...result.wheel,
            { date_time: new Date().toISOString(), values: wheelLiveKitEvent.values, comment: '' },
          ],
        };
        setResult(x);
      }
      setWheelValues(wheelLiveKitEvent.values);
      setIsClientSelecting(false);
    }
    onWheelLiveKitEventConsumed?.();
  }, [wheelLiveKitEvent, result, setResult, onWheelLiveKitEventConsumed]);

  const titleSize =
    'font-comfortaa font-bold text-blackBase text-sm md:text-lg lg:text-lx xl:text-2xl 2xl:text-3xl largePC:text-4xl';
  const textSize = 'font-comfortaa text-xs md:text-sm lg:text-base xl:text-base 2xl:text-base largePC:text-xl';
  const normalTextSize = `${textSize} font-comfortaa font-bold`;
  const commonClassButton = `px-[1rem] !text-center !content-center ${normalTextSize} text-balance cursor-pointer !border-width-none rounded-[10px]`;
  const classButton = `${commonClassButton} bg-[#FFFFFF] border-[#D3D3D3] border-width-[1px] text-black`;
  const classSelectedButton = `${commonClassButton} bg-[#C00000] text-white`;

  const showTip = useCallback(
    (tip: ITipItem) =>
      tip.richtext ? (
        <div
          key="tip_markDown"
          className={`w-full h-full flex flex-col ${tip.richtext.length < 360 ? 'justify-center' : 'pt-[1rem]'} px-[1rem] box-border ${textSize} gap-[2rem]`}
        >
          <ReactMarkdown>{tip.richtext}</ReactMarkdown>
        </div>
      ) : (
        <div key="tip_plain" className="w-full h-full flex flex-col items-center justify-center px-[1rem] box-border">
          <div
            key="tip_qw"
            className={` text-center font-comfortaa ${normalTextSize} font-bold `}
            style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}
            dangerouslySetInnerHTML={{ __html: tip.qwestion }}
          >
            {/* {tip.qwestion} */}
          </div>
          {tip.note.length > 0 && (
            <div
              key="tip_note"
              className={` text-center ${normalTextSize} mt-[6rem] font-normal`}
              style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}
              dangerouslySetInnerHTML={{ __html: tip.note }}
            ></div>
          )}
        </div>
      ),

    [normalTextSize],
  );

  const leftPanelitemWidth = 400; //breakpoint >= Breakpoint.Lg ? 500 : 200;

  const LeftPanel =
    wheelTips.length > 0 ? (
      <div
        className={classNames('h-full flex flex-col content-between rounded-[10px] bg-[#FDFDFD] mt-[0.25rem]', {
          'w-[350px] !min-w-[350px] !max-w-[350px]': breakpoint >= Breakpoint.Lg,
          'w-[200px] !min-w-[200px] !max-w-[200px]': breakpoint < Breakpoint.Lg,
        })}
      >
        {wheelTips[selectedTips].tips.length < 2 ? (
          <div className="w-full h-full items-center content-center justify-center ">
            {wheelTips[selectedTips].tips.length === 1 ? showTip(wheelTips[selectedTips].tips[0]) : <></>}
          </div>
        ) : (
          <TipPages
            classOuter={'w-full min-h-[100%] h-full flex flex-col items-center content-center box-border '}
            classIner={'w-full h-full flex overflow-x-hidden scroll-smooth justify-center !items-center'}
            classButtons={breakpoint >= Breakpoint.Lg ? 'w-12 h-12' : 'w-3 h-3'}
            titleKey={wheelTips[selectedTips].title}
            total={wheelTips[selectedTips].tips.length}
            component={(index) => showTip(wheelTips[selectedTips].tips[index])}
          />
        )}
      </div>
    ) : null;

  const onClick = useCallback(async () => {
    if (!showTips && !tipsLoading && wheelTips.length === 0) {
      setTipsLoading(true);
      const { items, index } = await loadWheelTips();
      setWheelTips(items);
      setSelectedTips(index);

      setTipsLoading(false);
    }
    setShowTips((v) => !v);
  }, [showTips, tipsLoading, wheelTips]);

  const withTips =
    wheelTips.length === 0 ? null : (
      <>
        {LeftPanel}
        <div className="w-full h-full flex flex-col gap-[1rem]">
          <div className="w-full h-[70%]">
            <WheelPanel
              values={wheelValues}
              small
              withTips
              onButtonTips={() => onClick()}
              showClientSelectingOverlay={isClientSelecting}
            />
          </div>
          <div className={'w-full h-[30%] grid grid-cols-3 grid-rows-[1fr_1fr_1fr] gap-[0.5rem] mx-[0.25rem] '}>
            {wheelTips.map((tip, index) => (
              <div
                key={`wt-${index}`}
                className={index === selectedTips ? classSelectedButton : classButton}
                onClick={() => setSelectedTips(index)}
              >
                {wheelTips[index].title}
              </div>
            ))}
          </div>
        </div>
      </>
    );
  const noTips = (
    <WheelPanel
      values={wheelValues}
      onButtonTips={() => onClick()}
      showClientSelectingOverlay={isClientSelecting}
    />
  );

  const initPanel = (
    <div className="sm:w-[30vw] lg:w-[15vw] md:min-w-[300px] h-full flex flex-col">
      <p className="flex mr-[3rem] ml-[1rem] mt-[1rem] font-comfortaa font-bold text-2xl text-white">
        Колесо жизненного баланса
      </p>
      <div className="text-base text-white p-[1rem]">
        {'Вы уверены, что хотите включить колесо жизненного баланса?'}
      </div>
      <div className="mt-auto flex flex-row">
        <Button
          type={'primary'}
          className="!text-sm xl:!text-base p-[6px_10px] !self-center"
          onClick={() => {
            doSocket();
            onToolActivated?.('life_balance_wheel');
            setActive(true);
          }}
        >
          {'Включить'}
        </Button>
        <div className="min-w-[48px] w-[48px]"></div>
      </div>
    </div>
  );

  return (
    <>
      {active ? (
        <div className="w-full h-full flex flex-row gap-[0.25rem] relative m-0 p-0">{showTips ? withTips : noTips}</div>
      ) : (
        initPanel
      )}
    </>
  );
};
